
import {
  Component,
  Inject,
  InjectReactive,
  Vue,
  Watch
} from 'vue-property-decorator';

import { NebraskaColors } from '@warrenbrasil/nebraska-tokens-web';
import {
  Button,
  BaseIcon,
  ContextColorProvider,
  ActionableHelperView
} from '@warrenbrasil/nebraska-web';

import { AccountBalance } from '../AccountBalance';
import { SingleInput } from '@/modules/transaction/components/SingleInput';
import { InvestmentContainerTemplate } from '../InvestmentContainerTemplate';
import { InvestmentTransactionNavbar } from '../InvestmentTransactionNavbar';
import { InvestmentSection } from '../InvestmentSection';
import { BankDetailsModal } from '../BankDetailsModal';
import { TradeTransactionSuccess } from './components/TradeTransactionSuccess';
import { FadeTransition } from '@/modules/common/components/FadeTransition';

import {
  trackInvestmentSuggestion,
  trackInputFeedback,
  trackNoMoneyWarning,
  trackInvestmentSuccess,
  trackDepositClick,
  trackTradeTransactionViewLoad
} from './tracking';
import { InvestmentTransactionContext } from '../../types';
import { WarrenModules } from '@/types/navigation';

@Component({
  components: {
    BaseIcon,
    AccountBalance,
    ContextColorProvider,
    ActionableHelperView,
    SingleInput,
    Button,
    InvestmentSection,
    InvestmentTransactionNavbar,
    InvestmentContainerTemplate,
    TradeTransactionSuccess,
    BankDetailsModal,
    FadeTransition
  }
})
export default class TradeTransaction extends Vue {
  @InjectReactive()
  private context!: InvestmentTransactionContext;

  @Inject()
  private fetchAccountData!: () => Promise<void>;

  @Inject()
  private processTransaction!: (value: number) => Promise<void>;

  @Inject()
  private refreshBalance!: () => Promise<void>;

  @Inject()
  private resetTransactionState!: () => Promise<void>;

  @Inject()
  private openBankDetailsModal!: () => void;

  @Inject()
  private closeBankDetailsModal!: () => void;

  private NebraskaColors = NebraskaColors;
  private hasInputError = false;
  private valueToInvest = 0;
  private errorMessage = '';

  private async created() {
    await this.fetchAccountData();

    if (!this.context.hasMoneyInAccount) {
      this.handleNoMoneyInAccount();
    }
    trackTradeTransactionViewLoad();
  }

  private handleSuggestionClick = (suggestionLabel: string) => {
    trackInvestmentSuggestion(suggestionLabel);
  };

  private submitInvestment() {
    trackDepositClick(this.valueToInvest);
    if (!this.canCreateTransactionToWallet()) return;
    this.processTransaction(this.valueToInvest);
  }

  private canCreateTransactionToWallet() {
    const totalBalance = this.context.totalBalance.value!;

    if (!this.valueToInvest || this.valueToInvest < 1) {
      this.setInputError(
        'Você precisa inserir um valor maior que R$ 1,00 para investir na Bolsa'
      );
      trackInputFeedback({
        isValueSmallerThanMinimum: true,
        value: this.valueToInvest
      });

      return false;
    }

    if (this.valueToInvest > totalBalance) {
      this.setInputError(
        'O valor informado é maior que o saldo disponível em conta'
      );
      trackInputFeedback({ value: this.valueToInvest });

      return false;
    }

    this.resetInputErrors();
    return true;
  }

  private get isButtonDisabled() {
    return this.valueToInvest === 0 || this.context.isCreatingTransaction;
  }

  private setInputError(errorMessage: string) {
    this.hasInputError = true;
    this.errorMessage = errorMessage;
  }

  private handleInputFocus() {
    this.resetInputErrors();
  }

  private resetInputErrors() {
    this.hasInputError = false;
    this.errorMessage = '';
    this.resetTransactionState();
  }

  public handleBackClick() {
    this.$router.back();
  }

  get showNavbarBackButton() {
    return Boolean(this.cameFromRoute);
  }

  private get cameFromRoute() {
    return this.$route.query.cameFrom as WarrenModules;
  }

  public handleCloseClick() {
    const redirectTo = this.cameFromRoute ?? WarrenModules.Trade;
    this.$router.replace({ name: redirectTo });
  }

  private handleNoMoneyInAccount() {
    trackNoMoneyWarning();
    this.openBankDetailsModal();
  }

  @Watch('context.isCreateTransactionSucceeded')
  private watchCreateTransactionSuccess(success: boolean) {
    if (!success) return;
    trackInvestmentSuccess(this.valueToInvest);
  }

  @Watch('context.isCreateTransactionFailed')
  private watchCreateTransaction(hasError: boolean) {
    if (!hasError) return;
    this.errorMessage = 'Ops! Aconteceu algum problema, tente novamente';
  }

  @Watch('valueToInvest')
  private watchValue() {
    this.resetInputErrors();
  }
}
